import { call } from '@advanza/api'
import { InputField } from '@advanza/input'
import { Button } from '@advanza/ui'
import {
    ChurnReasonThemeItem as ChurnReasonTheme,
    ChurnReasonThemeItemResponse,
    ChurnReasonThemesListResponse,
} from '@trustoo/types/api/controllers/churnReasonThemes'
import { useToast } from 'components/misc/Toaster'
import { hasAccess } from 'misc/user'
import { useEffect, useState } from 'react'

const LABEL_LANGUAGES = ['nl', 'nl-BE', 'fr', 'de', 'es'] as const

type LabelLanguage = (typeof LABEL_LANGUAGES)[number]

interface EditFields {
    label_en: string
    labels: Record<LabelLanguage, string>
}

const BASE_URL = 'office/churn-reason-themes'

const formatDate = (value: string | null) =>
    value === null ? '' : new Date(value).toLocaleString('nl-NL')

const editFieldsFor = (theme: ChurnReasonTheme): EditFields => ({
    label_en: theme.label_en,
    labels: Object.fromEntries(
        LABEL_LANGUAGES.map((language) => [language, theme.labels[language] ?? '']),
    ) as Record<LabelLanguage, string>,
})

const ChurnReasonThemesList = () => {
    const { addToast } = useToast()
    const hasWriteAccess = hasAccess('winback_offers', 'w')
    const [items, setItems] = useState<ChurnReasonTheme[]>([])
    const [loading, setLoading] = useState(true)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [editFields, setEditFields] = useState<EditFields | null>(null)
    const [expandedIds, setExpandedIds] = useState<number[]>([])
    const [isSaving, setIsSaving] = useState(false)

    const fetchItems = async () => {
        setLoading(true)
        try {
            const response: ChurnReasonThemesListResponse = await call(`${BASE_URL}/get-list`)
            setItems(response.items ?? [])
        } catch {
            addToast({ msg: 'Failed to load churn reason themes.', red: true })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchItems()
    }, [])

    const replaceItem = (item: ChurnReasonTheme) => {
        setItems((current) =>
            current.map((existing) =>
                existing.churn_reason_theme_id === item.churn_reason_theme_id ? item : existing,
            ),
        )
    }

    const post = async (url: string, json: object, successMessage: string) => {
        setIsSaving(true)
        try {
            const response: ChurnReasonThemeItemResponse = await call(url, { method: 'POST', json })
            replaceItem(response.item)
            addToast({ msg: successMessage, green: true })
            return true
        } catch (response) {
            const error =
                response && typeof response === 'object' && 'error' in response
                    ? String((response as { error: unknown }).error)
                    : 'Request failed.'
            addToast({ msg: error, red: true })
            return false
        } finally {
            setIsSaving(false)
        }
    }

    const approveTheme = (theme: ChurnReasonTheme) =>
        post(`${BASE_URL}/approve/${theme.churn_reason_theme_id}`, {}, 'Theme approved.')

    const startEdit = (theme: ChurnReasonTheme) => {
        setEditingId(theme.churn_reason_theme_id)
        setEditFields(editFieldsFor(theme))
    }

    const cancelEdit = () => {
        setEditingId(null)
        setEditFields(null)
    }

    const saveEdit = async () => {
        if (editingId === null || editFields === null || !editFields.label_en.trim()) {
            return
        }
        const saved = await post(`${BASE_URL}/save/${editingId}`, editFields, 'Saved.')
        if (saved) {
            cancelEdit()
        }
    }

    const toggleTexts = (themeId: number) => {
        setExpandedIds((current) =>
            current.includes(themeId)
                ? current.filter((id) => id !== themeId)
                : [...current, themeId],
        )
    }

    if (loading) {
        return <div>Loading...</div>
    }

    if (items.length === 0) {
        return <p>No themes yet.</p>
    }

    return (
        <table className="w-full border-collapse">
            <thead>
                <tr className="border-b-2 border-[#dddddd]">
                    <th className="p-2 text-left align-top">Theme</th>
                    <th className="p-2 text-left align-top">Labels</th>
                    <th className="p-2 text-left align-top">Usage</th>
                    <th className="p-2 text-left align-top">Status</th>
                    {hasWriteAccess && <th className="p-2 text-left align-top">Actions</th>}
                </tr>
            </thead>
            <tbody>
                {items.map((theme) => {
                    const isEditing =
                        editingId === theme.churn_reason_theme_id && editFields !== null
                    const isExpanded = expandedIds.includes(theme.churn_reason_theme_id)

                    return (
                        <tr key={theme.churn_reason_theme_id} className="border-b border-[#eeeeee]">
                            <td className="p-2 text-left align-top">
                                <div className="font-bold">
                                    {isEditing && editFields ? (
                                        <InputField
                                            value={editFields.label_en}
                                            onChange={(e) =>
                                                setEditFields({
                                                    ...editFields,
                                                    label_en: e.target.value,
                                                })
                                            }
                                        />
                                    ) : (
                                        theme.label_en
                                    )}
                                </div>
                                <div className="text-sm">
                                    <code>{theme.theme_key}</code> · #{theme.churn_reason_theme_id}
                                </div>
                                <div className="text-sm">
                                    Seeded by provider {theme.created_by_service_provider_id ?? '?'}{' '}
                                    on {formatDate(theme.created)}
                                </div>
                                {theme.sample_texts.length > 0 && (
                                    <div className="text-sm">
                                        <Button
                                            name="link"
                                            onClick={() =>
                                                toggleTexts(theme.churn_reason_theme_id)
                                            }>
                                            {isExpanded ? 'Hide texts' : 'Show texts'}
                                        </Button>
                                        {isExpanded && (
                                            <ul className="mt-2 flex flex-col gap-2">
                                                {theme.sample_texts.map((text) => (
                                                    <li key={text}>
                                                        <em>{text}</em>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}
                            </td>
                            <td className="p-2 text-left align-top">
                                {LABEL_LANGUAGES.map((language) => {
                                    const label = theme.labels[language]
                                    if (!isEditing && !label) {
                                        return null
                                    }
                                    return (
                                        <div key={language} className="flex items-center gap-2">
                                            <span className="w-12 text-sm">{language}</span>
                                            {isEditing && editFields ? (
                                                <InputField
                                                    value={editFields.labels[language]}
                                                    onChange={(e) =>
                                                        setEditFields({
                                                            ...editFields,
                                                            labels: {
                                                                ...editFields.labels,
                                                                [language]: e.target.value,
                                                            },
                                                        })
                                                    }
                                                />
                                            ) : (
                                                <span>{label}</span>
                                            )}
                                        </div>
                                    )
                                })}
                            </td>
                            <td className="p-2 text-left align-top">
                                <div>{theme.hits} matched</div>
                                <div>{theme.chosen} chosen on cancel</div>
                            </td>
                            <td className="p-2 text-left align-top">
                                {theme.approved_at === null ? (
                                    <span>Awaiting review</span>
                                ) : (
                                    <span>Approved {formatDate(theme.approved_at)}</span>
                                )}
                            </td>
                            {hasWriteAccess && (
                                <td className="p-2 text-left align-top">
                                    <div className="flex flex-col gap-2">
                                        {isEditing ? (
                                            <>
                                                <Button disabled={isSaving} onClick={saveEdit}>
                                                    Save
                                                </Button>
                                                <Button name="text" onClick={cancelEdit}>
                                                    Cancel
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                {theme.approved_at === null && (
                                                    <Button
                                                        disabled={isSaving}
                                                        onClick={() => approveTheme(theme)}>
                                                        Approve
                                                    </Button>
                                                )}
                                                <Button
                                                    name="text"
                                                    disabled={isSaving}
                                                    onClick={() => startEdit(theme)}>
                                                    Edit labels
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            )}
                        </tr>
                    )
                })}
            </tbody>
        </table>
    )
}

export default ChurnReasonThemesList
